<template>
  <Toolbar class="p-mb-4 p-mt-4 p-p-2">
    <template #start>
      <div style="font-size: 1.4em; color: inherit" class="p-p-2">{{ $t('Notifications') }}</div>
    </template>
    <template #end>
      <div class="p-p-1">
        <Button @click="saveSettings"
                :disabled="!$store.getters.isAdminOrHigher || locationIsLoading || fetchInFlight"
                :loading="locationIsLoading || fetchInFlight"
                class="p-button-raised"
                icon="ti-save"
                :label="$t('Save')"/>
      </div>
    </template>
  </Toolbar>

  <div class="p-grid">
    <div class="p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-2">
      <fieldset class="p-fieldset p-pb-4">
        <legend class="p-fieldset-legend p-p-2">
          {{ $t('smsSettingsTitle') }}
        </legend>

        <BillingFeatureNotice v-if="!canUseVisitSmsReminder"
                              class="p-pb-0"
                              feature="visit_sms_reminder"
                              :description="$t('smsSettingsDescription')"/>

        <template v-else>
          <div v-if="smsSenderNamesLoading" class="closed-state">
            <Spinner :isSmall="true"/>
          </div>

          <InlineNotice v-else-if="smsSenderNamesLoadFailed" severity="error" class="p-mt-3">
            {{ $t('Error') }}
          </InlineNotice>

          <template v-else>
            <div class="p-d-flex p-ai-center p-mt-3" v-if="smsSenderNames.length">
              <div class="p-d-flex p-ai-center">
                <InputSwitch id="use-branded-sms-sender-checkbox"
                             :modelValue="!!smsSenderNames.length && useBrandedSmsSender"
                             @update:modelValue="onBrandedSmsSenderChange"
                             :disabled="!smsSenderNames.length"/>
              </div>
              <label for="use-branded-sms-sender-checkbox"
                     class="switch-label"
                     :class="{'pointer': !!smsSenderNames.length}">
                {{ $t('useBrandedSmsSenderLabel') }}
              </label>
            </div>

            <NoticePlaceholder v-if="!smsSenderNames.length && !pendingSmsSenderName"
                               icon="ti-mobile"
                               :title="$t('noSmsSenderNamesTitle')"
                               :text="$t('noSmsSenderNamesText')"/>

            <div v-if="smsSenderNames.length" class="p-mt-5">
              <div class="p-float-label">
                <CustomDropdown id="sms-sender-name"
                                class="settings-input"
                                v-model="selectedSenderName"
                                @update:modelValue="onSmsSenderNameChange"
                                :options="smsSenderNames"
                                :disabled="!useBrandedSmsSender"
                                :filter="true"
                                :filterFields="['name']"
                                data-key="id"
                                :class="{'p-invalid': useBrandedSmsSender && senderNameRequired}">
                  <template #value="slotProps">
                    <div v-if="slotProps.value">
                      <span>{{ slotProps.value.name }}</span>
                    </div>
                    <span v-else style="visibility: hidden">.</span>
                  </template>
                  <template #option="slotProps">
                    <div>
                      <span>{{ slotProps.option.name }}</span>
                    </div>
                  </template>
                </CustomDropdown>
                <label for="sms-sender-name">
                  {{ $t('Sender Name') }}<span v-if="useBrandedSmsSender" class="warning-color">*</span>
                </label>
              </div>

              <small v-if="useBrandedSmsSender && senderNameRequired" class="p-invalid">{{ $t('Required field') }}</small>

              <small class="p-d-flex p-ai-center p-mt-1 p-text-secondary">
                <i class="ti-info-alt p-mr-1"/>
                {{ $t('smsSenderNameHelp') }}
              </small>
            </div>

            <InlineNotice v-if="pendingSmsSenderName"
                          variant="accent-only"
                          severity="info"
                          class="p-mt-3"
                          :title="$t('smsSenderNameApprovalTitle')">
              <strong>{{ pendingSmsSenderName }}</strong> — {{ $t('smsSenderNameApprovalText') }}
            </InlineNotice>

            <div v-if="$store.getters.isAdminOrHigher && !pendingSmsSenderName && !smsSenderNameLimitReached"
                 class="p-mt-3"
                 :class="{'p-d-flex p-jc-center': !smsSenderNames.length}">
              <Button type="button"
                      icon="ti-plus"
                      class="p-button-text"
                      :label="$t('addSmsSenderNameAction')"
                      @click="openSmsSendernameModal"/>
            </div>
          </template>

          <hr class="p-mt-4 p-mb-4">

          <div class="medium-bold-text p-d-flex p-ai-center">
            {{ $t('appointmentRemindersTitle') }}
          </div>

          <div class="p-d-flex p-ai-center p-mt-4">
            <div class="p-d-flex p-ai-center">
              <InputSwitch id="default-appointment-reminder-checkbox"
                           :modelValue="canUseVisitSmsReminder && defaultAppointmentReminder"
                           @update:modelValue="defaultAppointmentReminder = $event"
                           :disabled="!canUseVisitSmsReminder"/>
            </div>
            <label for="default-appointment-reminder-checkbox"
                   class="switch-label"
                   :class="{'pointer': canUseVisitSmsReminder}">
              {{ $t('appointmentReminderByDefaultLabel') }}
            </label>
            <i v-tooltip="$t('appointmentReminderByDefaultHelp')" class="ti-help-alt cursor-help"/>
          </div>

          <div class="p-d-flex p-ai-center p-mt-4">
            <div class="p-d-flex p-ai-center">
              <InputSwitch id="requires-parking-disc-checkbox"
                           :modelValue="canUseVisitSmsReminder && requiresParkingDisc"
                           @update:modelValue="requiresParkingDisc = $event"
                           :disabled="!canUseVisitSmsReminder"/>
            </div>
            <label for="requires-parking-disc-checkbox"
                   class="switch-label"
                   :class="{'pointer': canUseVisitSmsReminder}">
              {{ $t('addParkingDiscNoteLabel') }}
            </label>
          </div>

          <div class="p-d-flex p-ai-center p-mt-4" v-if="canUseBookingFromOffer">
            <div class="p-d-flex p-ai-center">
              <InputSwitch id="booking-appointment-reminder-checkbox"
                           :modelValue="canUseVisitSmsReminder && bookingAppointmentReminder"
                           @update:modelValue="bookingAppointmentReminder = $event"
                           :disabled="!canUseVisitSmsReminder"/>
            </div>
            <label for="booking-appointment-reminder-checkbox"
                   class="switch-label"
                   :class="{'pointer': canUseVisitSmsReminder}">
              {{ $t('estimateBookingAppointmentReminderLabel') }}
            </label>
            <i v-tooltip="$t('estimateBookingAppointmentReminderHelp')" class="ti-help-alt cursor-help"/>
          </div>
        </template>
      </fieldset>
    </div>

    <div class="p-col-12 p-sm-12 p-md-6 p-lg-6">
      <fieldset class="p-fieldset p-pb-4">
        <legend class="p-fieldset-legend p-p-2">{{ $t('messageLanguageTitle') }}</legend>

        <div class="p-mt-2">
          <div class="p-text-secondary">{{ $t('messageLanguageModeLabel') }}:</div>
          <ul class="p-d-flex p-ai-center p-flex-wrap p-mt-2">
            <li class="p-field-radiobutton p-mr-3">
              <RadioButton :id="'recipient-checkbox'"
                           value="Recipient"
                           v-model="templateLanguageMode"/>
              <label :for="'recipient-checkbox'" class="pointer">
                {{ $t('recipientLabel') }}
              </label>
            </li>
            <li class="p-field-radiobutton">
              <RadioButton :id="'location-checkbox'"
                           value="Location"
                           v-model="templateLanguageMode"/>
              <label :for="'location-checkbox'" class="pointer">
                {{ $t('Location') }}
              </label>
            </li>
          </ul>
          <small class="p-d-flex p-ai-center p-mt-1 p-text-secondary">
            <i class="ti-info-alt p-mr-1"/>
            {{ $t('messageLanguageModeHelp') }}
          </small>
          <div class="p-mt-2 p-text-secondary">
            <small>{{ $t('recipientLanguageModeHelp') }}</small>
          </div>
          <div class="p-text-secondary">
            <small>{{ $t('locationLanguageModeHelp') }}</small>
          </div>
        </div>
      </fieldset>
    </div>

  </div>

  <SmsSenderNameModal :visible="smsSenderNameModalVisible"
                      @update-items="onSmsSenderNameCreated"
                      @close="closeSmsSendernameModal"/>
</template>

<script>
import showErrorsMixins from "@/mixins/showErrorsMixins";
import formatMixins from "@/mixins/formatMixins";
import httpClient from "@/services/http.services";
import settings from "@/settings";
import NoticePlaceholder from "@/components/NoticePlaceholder";
import InlineNotice from "@/components/InlineNotice.vue";
import SmsSenderNameModal from "@/pages/account/components/SmsSenderNameModal";
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
// import BillingFeatureHint from "@/components/billing/BillingFeatureHint.vue";
import BillingFeatureNotice from "@/components/billing/BillingFeatureNotice.vue";

export default {
  components: { NoticePlaceholder, InlineNotice, SmsSenderNameModal, BillingFeatureNotice },
  emits: ['update-items'],
  mixins: [ showErrorsMixins, formatMixins, billingFeaturesMixins ],
  props: {
    locationData: {
      type: Object,
      default: null,
    },
    locationIsLoading: {
      type: Boolean,
      default: false
    },
  },
  watch: {
    'locationData.id'(id, previousId) {
      if (!id || id === previousId) return
      this.applySettings()
      if (this.canUseVisitSmsReminder) this.loadSmsSenderNames()
    },
    canUseVisitSmsReminder(enabled) {
      if (enabled && this.locationData?.id) this.loadSmsSenderNames()
    },
    '$store.state.firstLayerIsOpened'() {
      if (!this.$store.state.firstLayerIsOpened) {
        if (this.smsSenderNameModalVisible) {
          this.closeSmsSendernameModal()
        }
      }
    },
  },
  data() {
    return {
      // submitted: false,
      defaultAppointmentReminder: false,
      bookingAppointmentReminder: false,
      templateLanguageMode: null,
      requiresParkingDisc: false,
      useBrandedSmsSender: false,
      smsSenderNamesLoading: false,
      smsSenderNamesLoadFailed: false,
      smsSenderNameModalVisible: false,
      pendingSmsSenderName: null,
      smsSenderNameLimitReached: false,
      senderNameRequired: false,
      smsSenderRequestId: 0,
      fetchInFlight: false,
      selectedSenderName: null,
      smsSenderNames: [],
    }
  },
  mounted() {
    this.applySettings();

    if (this.canUseVisitSmsReminder) {
      this.loadSmsSenderNames();
    }
  },
  methods: {
    openSmsSendernameModal() {
      this.smsSenderNameModalVisible = true
      this.$store.commit('toggleFirstLayer', true)
    },
    closeSmsSendernameModal() {
      this.smsSenderNameModalVisible = false
      this.$store.commit('toggleFirstLayer', false)
    },
    onBrandedSmsSenderChange(enabled) {
      this.useBrandedSmsSender = enabled
      this.senderNameRequired = false
    },
    onSmsSenderNameChange(senderName) {
      this.selectedSenderName = senderName
      if (senderName?.id) this.senderNameRequired = false
    },
    async loadSmsSenderNames(applyLocationSettings = true) {
      if (!this.locationData?.id) return

      const requestId = ++this.smsSenderRequestId;
      const locationId = this.locationData.id;
      this.smsSenderNamesLoading = true;
      this.smsSenderNamesLoadFailed = false;
      try {
        const { data, status } = await httpClient.get('sms-sender/approved-names')
        if (requestId !== this.smsSenderRequestId || locationId !== this.locationData?.id) return

        if (status === 200 && data?.success) {
          this.smsSenderNames = data.items || [];
          this.pendingSmsSenderName = data.pending_name || null;
          this.smsSenderNameLimitReached = !!data.limit_reached;
        } else {
          this.smsSenderNamesLoadFailed = true;
        }

        if (!this.smsSenderNamesLoadFailed && applyLocationSettings) this.applySettings();

      } catch (e) {
        if (requestId !== this.smsSenderRequestId) return
        this.showError(e);
        this.smsSenderNamesLoadFailed = true;
      } finally {
        if (requestId === this.smsSenderRequestId) this.smsSenderNamesLoading = false;
      }
    },
    onSmsSenderNameCreated() {
      this.loadSmsSenderNames(false);
    },
    async saveSettings() {
      const locationId = this.locationData?.id
      if (!locationId || this.fetchInFlight) return

      if (this.canUseVisitSmsReminder && !this.smsSenderNamesLoading && !this.smsSenderNamesLoadFailed &&
          this.useBrandedSmsSender && !this.smsSenderNames.some(name => +name.id === +this.selectedSenderName?.id)) {
        this.senderNameRequired = true
        return false
      }

      this.fetchInFlight = true

      const payload = {
        location_id: locationId,
        template_language_mode: this.templateLanguageMode === 'Location' ? 2 : 1,
      }

      // Object.assign(payload, {
      //   sms_sender_id: this.useBrandedSmsSender && this.selectedSenderName?.id
      //       ? Number(this.selectedSenderName.id)
      //       : null,
      //   use_sms_sender: this.useBrandedSmsSender ? 1 : 0,
      // })

      // if (this.canUseVisitSmsReminder) {
      //   Object.assign(payload, {
      //     requires_parking_disc: this.requiresParkingDisc ? 1 : 0,
      //     default_appointment_reminder: this.defaultAppointmentReminder ? 1 : 0,
      //   })
      // }

      if (this.canUseVisitSmsReminder) {
        Object.assign(payload, {
          requires_parking_disc: this.requiresParkingDisc ? 1 : 0,
          default_appointment_reminder: this.defaultAppointmentReminder ? 1 : 0,
        })

        // Keep the saved sender selection if its list could not be loaded.
        if (!this.smsSenderNamesLoading && !this.smsSenderNamesLoadFailed) {
          Object.assign(payload, {
            sms_sender_id: this.smsSenderNames.length && this.useBrandedSmsSender && this.selectedSenderName?.id
                ? Number(this.selectedSenderName.id)
                : null,
            use_sms_sender: this.smsSenderNames.length && this.useBrandedSmsSender ? 1 : 0,
          })
        }
      }

      if (this.canUseBookingFromOffer) {
        Object.assign(payload, {
          booking_appointment_reminder: this.bookingAppointmentReminder ? 1 : 0,
        })
      }

      try {
        const { data, status } = await httpClient.post('location/settings-update', payload)
        if (status === 200 && data?.success) {
          this.$emit('update-items', locationId)
          this.$toast.add({ severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife })
        } else {
          this.showUnknownError()
        }
      } catch (err) {
        this.showError(err)
      } finally {
        this.fetchInFlight = false
      }
    },
    applySettings() {
      const s = this.locationData || {}
      if (!s) return

      if (this.smsSenderNames?.length && s.sms_sender_id && +s.sms_sender_id) {
        this.selectedSenderName = this.smsSenderNames.find(x => +x.id === +s.sms_sender_id) || null
      } else {
        this.selectedSenderName = null
      }

      this.useBrandedSmsSender = this.toBool(s.use_sms_sender) && !!this.selectedSenderName
      this.senderNameRequired = false
      this.requiresParkingDisc = this.toBool(s.requires_parking_disc)

      this.defaultAppointmentReminder = s.default_appointment_reminder === undefined
          ? true
          : this.toBool(s.default_appointment_reminder)

      this.bookingAppointmentReminder = s.booking_appointment_reminder === undefined
          ? true
          : this.toBool(s.booking_appointment_reminder)

      this.templateLanguageMode = +s.template_language_mode === 2 ? 'Location' : 'Recipient'
    },
    toBool(v) {
      return v === 1 || v === '1' || v === true;
    },
  },
}
</script>

<style scoped lang="scss">
.settings-input {
  width: 100%;
  max-width: 280px;
  min-width: 0;
  box-sizing: border-box;
}

.settings-input :deep(.p-inputnumber) {
  width: 100%;
  max-width: 280px;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
}

.settings-input :deep(.p-inputnumber-input),
.settings-input :deep(input.p-inputtext) {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.settings-input :deep(.p-inputnumber-button-group) {
  flex: 0 0 auto;
}

.p-col-12,
.p-sm-12,
.p-md-6,
.p-lg-6,
.p-fieldset {
  min-width: 0;
}
</style>
